import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Star, MapPin, Calendar, Award, IndianRupee, ArrowLeft, Share2, Heart, CheckCircle2, GraduationCap, Users, Loader2, ShieldCheck, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { fetchCollege, fetchAuthor } from '../lib/api';
import Seo from '../components/Seo';
import { buildCollegeSchema } from '../lib/schema';
import NotFound from './NotFound';

const CollegeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState(undefined);
  const [author, setAuthor] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setState(undefined);
    setAuthor(null);
    fetchCollege(id)
      .then((res) => {
        setState(res);
        const src = res.kind === 'parent' ? res.parent : res.data;
        if (src?.author_id) fetchAuthor(src.author_id).then(setAuthor).catch(() => {});
      })
      .catch(() => setState(null));
  }, [id]);

  if (state === undefined) return <div className="min-h-screen grid place-items-center bg-slate-50"><Loader2 className="w-6 h-6 animate-spin text-teal-600" /></div>;
  if (state === null) return <NotFound />;

  const { kind, data, parent, subpages } = state;
  const isSub = kind === 'sub';
  const activeSlug = isSub ? data.slug : parent.slug;
  const seoSrc = data;
  const college = parent;

  return (
    <div className="bg-slate-50 min-h-screen">
      <Seo
        title={seoSrc.meta_title || `${isSub ? data.tab_label + ' - ' : ''}${college.name} | Learndunia`}
        description={seoSrc.meta_description || college.about}
        keywords={seoSrc.meta_keywords}
        image={seoSrc.og_image || college.image}
        canonical={seoSrc.canonical_url || `https://learndunia.com/colleges/${activeSlug}`}
        jsonLd={buildCollegeSchema(college)}
      />

      <div className="relative h-64 md:h-80 overflow-hidden">
        <img src={college.image} alt={college.short} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/40 to-slate-900/10" />
        <div className="absolute inset-x-0 top-4 max-w-7xl mx-auto px-4">
          <button onClick={() => navigate('/colleges')} className="inline-flex items-center gap-1 text-white/90 hover:text-white text-sm bg-black/30 backdrop-blur px-3 py-1.5 rounded-full">
            <ArrowLeft className="w-4 h-4" /> Back to colleges
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 -mt-16 relative z-10">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-6">
          <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
            <Link to="/colleges" className="hover:text-teal-700">Colleges</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to={`/colleges/${college.slug}`} className="hover:text-teal-700">{college.short || college.name}</Link>
            {isSub && (<><ChevronRight className="w-3 h-3" /><span className="text-slate-700">{data.tab_label}</span></>)}
          </div>
          <div className="flex flex-col md:flex-row items-start gap-5">
            <img src={college.logo} alt="logo" className="w-20 h-20 rounded-xl border-4 border-white shadow-md" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className="bg-teal-100 text-teal-800 hover:bg-teal-100 border-teal-200">NIRF Rank #{college.ranking}</Badge>
                <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100 border-orange-200">{college.accreditation}</Badge>
                <Badge variant="outline">{college.type}</Badge>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mt-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{college.name}</h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600 mt-2">
                <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" /> {college.location}</span>
                <span className="inline-flex items-center gap-1"><Calendar className="w-4 h-4" /> Est. {college.established}</span>
                <span className="inline-flex items-center gap-1">
                  <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                  <b className="text-slate-900">{college.rating}</b> ({college.reviews} reviews)
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setSaved(!saved)} className={saved ? 'text-orange-600 border-orange-300' : ''}>
                <Heart className={`w-4 h-4 mr-1.5 ${saved ? 'fill-orange-500 text-orange-500' : ''}`} /> {saved ? 'Saved' : 'Save'}
              </Button>
              <Button variant="outline"><Share2 className="w-4 h-4 mr-1.5" /> Share</Button>
              <Button className="bg-orange-500 hover:bg-orange-600 text-white">Apply Now</Button>
            </div>
          </div>

          {!isSub && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
              {[
                { icon: IndianRupee, label: 'Fees', value: college.fees },
                { icon: Award, label: 'Accreditation', value: college.accreditation },
                { icon: Users, label: 'Reviews', value: `${college.reviews}` },
                { icon: GraduationCap, label: 'Courses', value: `${(college.courses || []).length}+` },
              ].map((h, i) => (
                <div key={i} className="bg-slate-50 rounded-xl p-3 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-teal-100 grid place-items-center"><h.icon className="w-5 h-5 text-teal-700" /></div>
                  <div>
                    <div className="text-[11px] uppercase text-slate-500 tracking-wide">{h.label}</div>
                    <div className="text-sm font-semibold text-slate-900">{h.value}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="mt-6 bg-white border border-slate-200 rounded-xl p-1 overflow-x-auto flex gap-1">
          <Link to={`/colleges/${college.slug}`}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${!isSub ? 'bg-teal-600 text-white' : 'text-slate-700 hover:bg-slate-100'}`}>
            Overview
          </Link>
          {subpages.map((sp) => (
            <Link key={sp.id} to={`/colleges/${sp.slug}`}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${isSub && sp.slug === activeSlug ? 'bg-teal-600 text-white' : 'text-slate-700 hover:bg-slate-100'}`}>
              {sp.tab_label}
            </Link>
          ))}
        </div>

        <div className="mt-6 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-6">
            {isSub ? (
              <>
                <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{data.tab_label}</h2>
                {data.content ? <div className="prose prose-slate max-w-none mt-4" dangerouslySetInnerHTML={{ __html: data.content }} /> : <p className="text-slate-500 mt-3">Content coming soon.</p>}
              </>
            ) : (
              <>
                <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>About {college.short}</h2>
                <p className="mt-3 text-slate-700 leading-relaxed">{college.about}</p>
                {college.content && <div className="prose prose-slate max-w-none mt-5" dangerouslySetInnerHTML={{ __html: college.content }} />}
                <div className="mt-6">
                  <h3 className="font-semibold text-slate-900 mb-3">Key Highlights</h3>
                  <ul className="space-y-2">
                    {(college.highlights || []).map((h) => (
                      <li key={h} className="flex items-start gap-2 text-sm text-slate-700">
                        <CheckCircle2 className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" /> {h}
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}
            {author && (
              <div className="mt-8 pt-6 border-t border-slate-100 flex items-center gap-3">
                <img src={author.avatar} alt={author.name} className="w-12 h-12 rounded-full object-cover" />
                <div>
                  <div className="text-xs text-slate-500 inline-flex items-center gap-1">
                    Written & fact-checked by {author.is_fact_checker && <ShieldCheck className="w-3 h-3 text-teal-600" />}
                  </div>
                  <Link to={`/author/${author.slug}`} className="font-semibold text-slate-900 hover:text-teal-700">{author.name}</Link>
                  <div className="text-xs text-slate-500">{author.designation}</div>
                </div>
              </div>
            )}
          </div>
          <div className="bg-gradient-to-br from-teal-600 to-teal-700 text-white rounded-2xl p-6 h-fit">
            <h3 className="font-bold text-lg" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Get Free Counselling</h3>
            <p className="text-teal-50 text-sm mt-1">Talk to our expert counsellor about {college.short}.</p>
            <div className="mt-4 space-y-2">
              <input placeholder="Your Name" className="w-full px-3 py-2.5 rounded-lg text-slate-900 text-sm outline-none" />
              <input placeholder="Mobile Number" className="w-full px-3 py-2.5 rounded-lg text-slate-900 text-sm outline-none" />
              <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white">Request Callback</Button>
            </div>
          </div>
        </div>
        <div className="h-16" />
      </div>
    </div>
  );
};

export default CollegeDetail;
